import { useEffect, useState, useRef } from "react";

interface UseLimitVisibleOptions {
  visibleFor: number;
  id: string;
}

const useLimitVisible = ({ visibleFor, id }: UseLimitVisibleOptions) => {
  const [count, setCount] = useState<number>(0);
  const [shouldShow, setShouldShow] = useState<boolean>(true);
  const elementRef = useRef<HTMLDivElement>(null);
  const isCountingRef = useRef<boolean>(false);

  useEffect(() => {
    const storedCount = localStorage.getItem(`limitVisibleCount_${id}`);
    if (storedCount) {
      const parsedCount = parseInt(storedCount, 10);
      setCount(parsedCount);
      setShouldShow(parsedCount <= visibleFor);
    }
  }, [id, visibleFor]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isCountingRef.current) {
          const newCount = count + 1;
          setCount(newCount);
          if (newCount <= visibleFor)
            localStorage.setItem(
              `limitVisibleCount_${id}`,
              newCount.toString()
            );

          isCountingRef.current = true;
          setShouldShow(newCount <= visibleFor);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [count, visibleFor, id]);

  return { elementRef, shouldShow };
};

export default useLimitVisible;
